import { useState, useMemo, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { KeenIcon } from '@/components';
import {
  useCreateKYCMutation,
  useGetPendingEntitiesQuery,
  useUpdateKYCMutation,
} from '@/services/KYCService';
import { useSnackbar } from 'notistack';
import { KYCType } from '@/constant';
import { VAT_PATTERNS } from '@/constants/vatPatterns';
import countries from 'world-countries';
import clsx from 'clsx';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { resolveCountryToIso2 } from '@/utils/countryName';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserKYCData } from '@/types/user-kyc.types';

interface AddKYCFormProps {
  mode?: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

interface KYCFormValues {
  entityType: '' | KYCType.HARBOR | KYCType.CLUB | KYCType.SERVICE_PROVIDER;
  entityId: string;
  legalName: string;
  businessId: string;
  vatId: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  accountHolderName: string;
  accountNumber: string;
  iban: string;
  bic: string;
  bankName: string;
}

const AddKYCForm = ({ mode = 'create', onSuccess }: AddKYCFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [createKYC, { isLoading: isCreating }] = useCreateKYCMutation();
  const [updateKYC, { isLoading: isUpdating }] = useUpdateKYCMutation();

  // Get KYC data from navigation state (no API call needed)
  const navState = location.state as { kycData?: UserKYCData } | null;
  const kycData = navState?.kycData || null;

  // Get user roles from auth slice
  const userRoles = useSelector(
    (state: RootState) => state.auth.userData?.roles || []
  );

  const isSubmitting = isCreating || isUpdating;
  const isViewMode = mode === 'view';

  // Determine which entity types to show based on user roles
  const availableEntityTypes = useMemo(() => {
    const types: Array<
      KYCType.HARBOR | KYCType.CLUB | KYCType.SERVICE_PROVIDER
    > = [];

    if (userRoles.includes('marina_manager')) {
      types.push(KYCType.HARBOR);
    }
    if (userRoles.includes('yacht_club_admin')) {
      types.push(KYCType.CLUB);
    }
    if (userRoles.includes('service_provider')) {
      types.push(KYCType.SERVICE_PROVIDER);
    }

    return types;
  }, [userRoles]);

  const [isEntityTypeDropdownOpen, setIsEntityTypeDropdownOpen] =
    useState(false);
  const [isEntityDropdownOpen, setIsEntityDropdownOpen] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [currentEntityType, setCurrentEntityType] = useState<
    '' | KYCType.HARBOR | KYCType.CLUB | KYCType.SERVICE_PROVIDER
  >('');
  const [entitySearchTerm, setEntitySearchTerm] = useState('');
  const [activeEntityOption, setActiveEntityOption] = useState(-1);
  const [pendingEntities, setPendingEntities] = useState<
    Array<{ entity_type: string; entity_id: string; name: string }>
  >([]);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const entityDropdownRef = useRef<HTMLDivElement>(null);

  const { data: entitiesResponse, isLoading: isLoadingEntities } =
    useGetPendingEntitiesQuery(
      currentEntityType as
        | KYCType.HARBOR
        | KYCType.CLUB
        | KYCType.SERVICE_PROVIDER,
      {
        skip: !currentEntityType,
      }
    );

  useEffect(() => {
    if (entitiesResponse) {
      // The API now returns the array directly
      setPendingEntities(entitiesResponse);
    } else {
      setPendingEntities([]);
    }
  }, [entitiesResponse]);

  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) => a.name.common.localeCompare(b.name.common)),
    []
  );

  const filteredEntities = useMemo(() => {
    if (!entitySearchTerm) return pendingEntities;
    return pendingEntities.filter((entity) =>
      entity.name.toLowerCase().includes(entitySearchTerm.toLowerCase())
    );
  }, [pendingEntities, entitySearchTerm]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        entityType:
          mode === 'edit'
            ? Yup.string()
            : Yup.string()
                .required('Entity type is required')
                .oneOf(
                  [KYCType.HARBOR, KYCType.CLUB, KYCType.SERVICE_PROVIDER],
                  'Invalid entity type'
                ),
        entityId:
          mode === 'edit'
            ? Yup.string()
            : Yup.string().required('Entity is required'),
        legalName: Yup.string()
          .required('Legal name is required')
          .min(2, 'Legal name must be at least 2 characters')
          .max(100, 'Legal name must not exceed 100 characters'),
        businessId: Yup.string()
          .required('Business ID is required')
          .min(5, 'Business ID must be at least 5 characters')
          .max(20, 'Business ID must not exceed 20 characters'),
        vatId: Yup.string().test(
          'vat-format',
          'Invalid VAT ID format',
          function (value) {
            const { country } = this.parent;
            if (!value) return true;
            if (country) {
              const patternInfo = VAT_PATTERNS[country];
              if (patternInfo) {
                if (patternInfo.pattern.test(value)) return true;
                const example = patternInfo.example;
                return this.createError({
                  message: example
                    ? `Invalid VAT ID format. Example: ${example}`
                    : 'Invalid VAT ID format',
                });
              }
            }
            const isGeneralValid = value.length >= 5 && value.length <= 20;
            if (!isGeneralValid) {
              return this.createError({
                message: 'Invalid VAT ID format',
              });
            }
            return true;
          }
        ),
        addressLine1: Yup.string()
          .required('Address Line 1 is required')
          .min(5, 'Address Line 1 must be at least 5 characters')
          .max(100, 'Address Line 1 must not exceed 100 characters'),
        addressLine2: Yup.string().max(
          100,
          'Address Line 2 must not exceed 100 characters'
        ),
        postalCode: Yup.string()
          .required('Postal code is required')
          .matches(/^[A-Z0-9]{5}$/i, 'Invalid postal code format'),
        city: Yup.string()
          .required('City is required')
          .min(2, 'City must be at least 2 characters')
          .max(50, 'City must not exceed 50 characters'),
        state: Yup.string().max(50, 'State must not exceed 50 characters'),
        country: Yup.string()
          .required('Country is required')
          .oneOf(
            sortedCountries.map((c) => c.cca2),
            'Invalid country'
          ),
        accountHolderName: Yup.string()
          .required('Account holder name is required')
          .matches(
            /^[\p{L}\s]+$/u,
            'Account holder name must contain only letters and spaces'
          )
          .min(2, 'Account holder name must be at least 2 characters')
          .max(100, 'Account holder name must not exceed 100 characters'),
        accountNumber: Yup.string()
          .required('Account number is required')
          .min(4, 'Account number must be at least 4 characters')
          .max(34, 'Account number must not exceed 34 characters'),
        iban: Yup.string()
          .required('IBAN is required')
          .test(
            'iban-alphanumeric',
            'IBAN must contain only letters and numbers',
            (value) => {
              if (!value) return false;
              return /^[A-Za-z0-9]+$/.test(value);
            }
          )
          .test(
            'iban-length',
            'IBAN must be between 15 and 34 characters',
            (value) => {
              if (!value) return false;
              return value.length >= 15 && value.length <= 34;
            }
          )
          .transform((value) => value?.toUpperCase()),
        bic: Yup.string()
          .required('BIC is required')
          .test('bic-format', 'Invalid BIC format', function (value) {
            if (!value) return false;
            const bicPattern = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
            if (bicPattern.test(value)) return true;
            return this.createError({
              message: 'Invalid BIC format. Example: NDEAFIHH',
            });
          })
          .transform((value) => value?.toUpperCase()),
        bankName: Yup.string()
          .required('Bank name is required')
          .min(2, 'Bank name must be at least 2 characters')
          .max(100, 'Bank name must not exceed 100 characters'),
      }),
    [sortedCountries, mode]
  );

  // Compute initial values reactively
  const initialValues = useMemo(() => {
    if (kycData && (mode === 'edit' || mode === 'view')) {
      // API returns plaintext bank account details
      return {
        entityType: (kycData.entity_type || '') as
          | ''
          | KYCType.HARBOR
          | KYCType.CLUB
          | KYCType.SERVICE_PROVIDER,
        entityId: kycData.entity_id || '',
        legalName: kycData.legal_name || '',
        businessId: kycData.business_id || '',
        vatId: kycData.vat_id || '',
        addressLine1: kycData.address_line_1 || '',
        addressLine2: kycData.address_line_2 || '',
        postalCode: kycData.postal_code || '',
        city: kycData.city || '',
        state: kycData.state || '',
        country: resolveCountryToIso2(kycData.country) || 'FI',
        accountHolderName: kycData.account_holder_name || '',
        accountNumber: kycData.account_number || '',
        iban: kycData.iban || '',
        bic: kycData.bic || '',
        bankName: kycData.bank_name || '',
      };
    }

    return {
      entityType: '' as
        | ''
        | KYCType.HARBOR
        | KYCType.CLUB
        | KYCType.SERVICE_PROVIDER,
      entityId: '',
      legalName: '',
      businessId: '',
      vatId: '',
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      city: '',
      state: '',
      country: 'FI', // Default to Finland
      accountHolderName: '',
      accountNumber: '',
      iban: '',
      bic: '',
      bankName: '',
    };
  }, [kycData, mode]);

  const formik = useFormik<KYCFormValues>({
    initialValues: initialValues,
    validationSchema: mode === 'view' ? undefined : validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (mode === 'view') {
        return;
      }

      // In edit mode, get entity_type and entity_id from existing data
      // In create mode, require entityType
      if (mode === 'edit') {
        if (!id || !kycData) {
          enqueueSnackbar('KYC data not found', {
            variant: 'error',
          });
          return;
        }
      } else {
        // In create mode, validate entityType and entityId
        if (
          !values.entityType ||
          (values.entityType as string) === '' ||
          ![KYCType.HARBOR, KYCType.CLUB, KYCType.SERVICE_PROVIDER].includes(
            values.entityType as KYCType
          )
        ) {
          enqueueSnackbar('Entity type is required', {
            variant: 'error',
          });
          return;
        }
        if (!values.entityId || values.entityId.trim() === '') {
          enqueueSnackbar('Entity is required', {
            variant: 'error',
          });
          return;
        }
      }

      try {
        // Backend encrypts sensitive fields at rest; send plaintext only.
        let entityType: 'harbor' | 'club' | 'service_provider';

        if (mode === 'edit') {
          entityType = kycData!.entity_type;
        } else {
          // In create mode, ensure entityType is set and valid
          const selectedEntityType = values.entityType as string;
          if (
            !selectedEntityType ||
            selectedEntityType === '' ||
            (selectedEntityType !== KYCType.HARBOR &&
              selectedEntityType !== KYCType.CLUB &&
              selectedEntityType !== KYCType.SERVICE_PROVIDER)
          ) {
            enqueueSnackbar('Entity type is required', {
              variant: 'error',
            });
            return;
          }
          // Convert enum to string value for API (enum values are already strings)
          entityType = selectedEntityType as
            | 'harbor'
            | 'club'
            | 'service_provider';
        }

        // Ensure entityType is set before creating payload
        if (!entityType) {
          enqueueSnackbar('Entity type is required', {
            variant: 'error',
          });
          return;
        }

        const payload = {
          entity_type: entityType,
          entity_id: mode === 'edit' ? kycData!.entity_id : values.entityId,
          legal_name: values.legalName,
          business_id: values.businessId,
          vat_id: values.vatId || null,
          address_line_1: values.addressLine1,
          address_line_2: values.addressLine2,
          postal_code: values.postalCode,
          city: values.city,
          state: values.state,
          country: values.country,
          account_holder_name: values.accountHolderName,
          account_number: values.accountNumber,
          iban: values.iban.toUpperCase(),
          bic: values.bic.toUpperCase(),
          bank_name: values.bankName,
        };

        if (mode === 'edit' && id) {
          await updateKYC({ id, data: payload }).unwrap();
          enqueueSnackbar('KYC updated successfully', {
            variant: 'success',
          });
        } else {
          await createKYC(payload).unwrap();
          enqueueSnackbar('KYC submitted successfully', {
            variant: 'success',
          });
        }

        // Navigate after successful update/create
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/user-kyc');
        }
      } catch (error: unknown) {
        const errorMessage =
          (error as { data?: { message?: string } })?.data?.message ||
          (mode === 'edit' ? 'Failed to update KYC' : 'Failed to submit KYC');
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }
    },
  });

  // Set current entity type when kycData is available
  useEffect(() => {
    if (
      kycData &&
      kycData.entity_type &&
      (mode === 'edit' || mode === 'view')
    ) {
      setCurrentEntityType(
        kycData.entity_type as
          | KYCType.HARBOR
          | KYCType.CLUB
          | KYCType.SERVICE_PROVIDER
      );
    }
  }, [kycData, mode]);

  useEffect(() => {
    if (
      formik.values.entityType &&
      formik.values.entityType !== currentEntityType &&
      (formik.values.entityType === KYCType.HARBOR ||
        formik.values.entityType === KYCType.CLUB ||
        formik.values.entityType === KYCType.SERVICE_PROVIDER)
    ) {
      setCurrentEntityType(formik.values.entityType);
    }
  }, [formik.values.entityType, currentEntityType]);

  const handleCancel = () => {
    setCurrentEntityType('');
    setPendingEntities([]);
    setEntitySearchTerm('');
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/user-kyc');
    }
  };

  // Show error if in edit/view mode but no data available
  if ((mode === 'edit' || mode === 'view') && !kycData) {
    return (
      <div>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            KYC data not found. Please navigate from the KYC list.
          </p>
          <button
            type="button"
            onClick={() => navigate('/user-kyc')}
            className="btn btn-sm btn-primary"
          >
            Back to KYC List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <form
        className="mt-4 space-y-4"
        onSubmit={formik.handleSubmit}
        aria-label="Add KYC form"
      >
        {mode === 'create' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Entity Type and Entity Selection */}
            {/* Entity Type */}
            <div className="form-control">
              <label htmlFor="entityType" className="label">
                <span className="label-text">Entity Type</span>
              </label>
              <div className="relative mt-1">
                <button
                  type="button"
                  id="entityType"
                  onClick={() =>
                    !isViewMode &&
                    setIsEntityTypeDropdownOpen(!isEntityTypeDropdownOpen)
                  }
                  onBlur={() =>
                    setTimeout(() => setIsEntityTypeDropdownOpen(false), 200)
                  }
                  disabled={isViewMode}
                  className="w-full h-11 px-5 py-3 text-left bg-[#DCE1E759] dark:bg-white/20 dark:text-white border border-[#DCE1E7] dark:border-white/20 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-secondary focus:bg-white dark:focus:bg-white/20 transition-all flex items-center justify-between contrast:bg-white/25 contrast:text-white contrast:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-expanded={isEntityTypeDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span
                    className={
                      formik.values.entityType
                        ? 'text-gray-700 dark:text-white contrast:text-white'
                        : 'text-gray-500 dark:text-white contrast:text-white'
                    }
                  >
                    {formik.values.entityType === KYCType.HARBOR
                      ? 'Harbor'
                      : formik.values.entityType === KYCType.CLUB
                        ? 'Club'
                        : formik.values.entityType === KYCType.SERVICE_PROVIDER
                          ? 'Service Provider'
                          : 'Select entity type'}
                  </span>
                  {!isViewMode && (
                    <KeenIcon
                      icon={isEntityTypeDropdownOpen ? 'up' : 'down'}
                      className="text-gray-400"
                    />
                  )}
                </button>
                {isEntityTypeDropdownOpen && !isViewMode && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary contrast:bg-white/25 contrast:backdrop-blur-md contrast:border-white/20 border border-gray-200 dark:border-white/20 rounded-lg shadow-lg max-h-[250px] overflow-y-scroll">
                    {availableEntityTypes.includes(KYCType.HARBOR) && (
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue('entityType', KYCType.HARBOR);
                          setCurrentEntityType(KYCType.HARBOR);
                          setIsEntityTypeDropdownOpen(false);
                          formik.setFieldValue('entityId', '');
                          setPendingEntities([]);
                          setEntitySearchTerm('');
                        }}
                        className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-secondary/20 contrast:text-white contrast:hover:bg-white/20 transition-colors"
                      >
                        Harbor
                      </button>
                    )}
                    {availableEntityTypes.includes(KYCType.CLUB) && (
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue('entityType', KYCType.CLUB);
                          setCurrentEntityType(KYCType.CLUB);
                          setIsEntityTypeDropdownOpen(false);
                          formik.setFieldValue('entityId', '');
                          setPendingEntities([]);
                          setEntitySearchTerm('');
                        }}
                        className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-secondary/20 contrast:text-white contrast:hover:bg-white/20 transition-colors"
                      >
                        Club
                      </button>
                    )}
                    {availableEntityTypes.includes(
                      KYCType.SERVICE_PROVIDER
                    ) && (
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue(
                            'entityType',
                            KYCType.SERVICE_PROVIDER
                          );
                          setCurrentEntityType(KYCType.SERVICE_PROVIDER);
                          setIsEntityTypeDropdownOpen(false);
                          formik.setFieldValue('entityId', '');
                          setPendingEntities([]);
                          setEntitySearchTerm('');
                        }}
                        className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-secondary/20 contrast:text-white contrast:hover:bg-white/20 transition-colors"
                      >
                        Service Provider
                      </button>
                    )}
                  </div>
                )}
              </div>
              {formik.touched.entityType && formik.errors.entityType && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.entityType}
                </p>
              )}
            </div>

            {/* Entity Selection */}
            <div
              className="form-control"
              ref={entityDropdownRef}
              onBlur={(e) => {
                const nextFocusedNode = e.relatedTarget as Node | null;
                if (!e.currentTarget.contains(nextFocusedNode)) {
                  setIsEntityDropdownOpen(false);
                  setActiveEntityOption(-1);
                  setEntitySearchTerm('');
                }
              }}
            >
              <label htmlFor="entityId" className="label">
                <span className="label-text">Entity</span>
              </label>
              <div className="relative mt-1">
                <button
                  type="button"
                  id="entityId"
                  role="combobox"
                  aria-expanded={isEntityDropdownOpen}
                  aria-haspopup="listbox"
                  aria-controls="entity-listbox"
                  onClick={() => setIsEntityDropdownOpen(!isEntityDropdownOpen)}
                  disabled={
                    !formik.values.entityType || isLoadingEntities || isViewMode
                  }
                  className="w-full h-11 px-5 py-3 text-left bg-[#DCE1E759] dark:bg-white/20 dark:text-white border border-[#DCE1E7] dark:border-white/20 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-secondary focus:bg-white dark:focus:bg-white/20 transition-all flex items-center justify-between contrast:bg-white/25 contrast:text-white contrast:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span
                    className={
                      formik.values.entityId
                        ? 'text-gray-700 dark:text-white contrast:text-white'
                        : 'text-gray-500 dark:text-white contrast:text-white'
                    }
                  >
                    {formik.values.entityId
                      ? filteredEntities.find(
                          (e) => e.entity_id === formik.values.entityId
                        )?.name || 'Select entity'
                      : 'Select entity'}
                  </span>
                  {!isViewMode && (
                    <KeenIcon
                      icon={isEntityDropdownOpen ? 'up' : 'down'}
                      className="text-gray-400"
                    />
                  )}
                </button>
                {isEntityDropdownOpen &&
                  formik.values.entityType &&
                  !isViewMode && (
                    <ul
                      role="listbox"
                      id="entity-listbox"
                      className="absolute z-10 w-full mt-1 bg-white dark:bg-primary contrast:bg-white/25 contrast:backdrop-blur-md border border-gray-300 dark:border-gray-700 contrast:border-white/20 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      <div className="p-2">
                        <input
                          type="text"
                          value={entitySearchTerm}
                          onChange={(e) => setEntitySearchTerm(e.target.value)}
                          placeholder="Search entity..."
                          className="w-full h-[40px] text-sm py-2 text-black dark:text-white contrast:text-white dark:placeholder:!text-white/70 contrast:placeholder:!text-white/70 bg-[#DCE1E759] border border-[#DCE1E7] rounded-[8px] px-3"
                        />
                      </div>
                      {filteredEntities.length > 0 ? (
                        filteredEntities.map((entity, index) => (
                          <li
                            key={entity.entity_id}
                            role="option"
                            aria-selected={activeEntityOption === index}
                            className={`px-4 py-2 text-black dark:text-white contrast:text-white cursor-pointer ${
                              activeEntityOption === index
                                ? 'bg-gray-100 dark:bg-gray-700 contrast:bg-white/20'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 contrast:hover:bg-white/20'
                            }`}
                            tabIndex={0}
                            onClick={() => {
                              formik.setFieldValue(
                                'entityId',
                                entity.entity_id
                              );
                              setIsEntityDropdownOpen(false);
                              setEntitySearchTerm('');
                              setActiveEntityOption(-1);
                            }}
                          >
                            {entity.name}
                          </li>
                        ))
                      ) : (
                        <li
                          className="px-4 py-2 text-gray-500 dark:text-gray-600 contrast:text-gray-400"
                          role="option"
                        >
                          {isLoadingEntities
                            ? 'Loading...'
                            : 'No entities found'}
                        </li>
                      )}
                    </ul>
                  )}
              </div>
              {formik.touched.entityId && formik.errors.entityId && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.entityId}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Legal Name */}
        <div className="form-control">
          <label htmlFor="legalName" className="label">
            <span className="label-text">Legal Name</span>
          </label>
          <input
            id="legalName"
            name="legalName"
            type="text"
            autoComplete="off"
            placeholder="Enter legal name"
            disabled={isSubmitting || isViewMode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.legalName}
            className={clsx(
              'input input-bordered w-full',
              formik.touched.legalName &&
                formik.errors.legalName &&
                'input-error'
            )}
          />
          {formik.touched.legalName && formik.errors.legalName && (
            <p className="mt-1 text-sm text-red-600">
              {formik.errors.legalName}
            </p>
          )}
        </div>

        {/* Business ID and VAT ID */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="form-control">
            <label htmlFor="businessId" className="label">
              <span className="label-text">Business ID</span>
            </label>
            <input
              id="businessId"
              name="businessId"
              type="text"
              autoComplete="off"
              placeholder="Enter business ID"
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.businessId}
              className={clsx(
                'input input-bordered w-full',
                formik.touched.businessId &&
                  formik.errors.businessId &&
                  'input-error'
              )}
            />
            {formik.touched.businessId && formik.errors.businessId && (
              <p className="mt-1 text-sm text-red-600">
                {formik.errors.businessId}
              </p>
            )}
          </div>
          <div className="form-control">
            <label htmlFor="vatId" className="label">
              <span className="label-text">VAT ID</span>
            </label>
            <input
              id="vatId"
              name="vatId"
              type="text"
              autoComplete="off"
              placeholder="Enter VAT ID"
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.vatId}
              className={clsx(
                'input input-bordered w-full',
                formik.touched.vatId && formik.errors.vatId && 'input-error'
              )}
            />
            {formik.touched.vatId && formik.errors.vatId && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.vatId}</p>
            )}
          </div>
        </div>

        {/* Address Line 1 */}
        <div className="form-control">
          <label htmlFor="addressLine1" className="label">
            <span className="label-text">Address Line 1</span>
          </label>
          <input
            id="addressLine1"
            name="addressLine1"
            type="text"
            autoComplete="off"
            placeholder="Enter address line 1"
            disabled={isSubmitting || isViewMode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.addressLine1}
            className={clsx(
              'input input-bordered w-full',
              formik.touched.addressLine1 &&
                formik.errors.addressLine1 &&
                'input-error'
            )}
          />
          {formik.touched.addressLine1 && formik.errors.addressLine1 && (
            <p className="mt-1 text-sm text-red-600">
              {formik.errors.addressLine1}
            </p>
          )}
        </div>

        {/* Address Line 2 */}
        <div className="form-control">
          <label htmlFor="addressLine2" className="label">
            <span className="label-text">Address Line 2</span>
          </label>
          <input
            id="addressLine2"
            name="addressLine2"
            type="text"
            autoComplete="off"
            placeholder="Enter address line 2 (optional)"
            disabled={isSubmitting || isViewMode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.addressLine2}
            className={clsx(
              'input input-bordered w-full',
              formik.touched.addressLine2 &&
                formik.errors.addressLine2 &&
                'input-error'
            )}
          />
          {formik.touched.addressLine2 && formik.errors.addressLine2 && (
            <p className="mt-1 text-sm text-red-600">
              {formik.errors.addressLine2}
            </p>
          )}
        </div>

        {/* Postal Code, City, State */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="form-control">
            <label htmlFor="postalCode" className="label">
              <span className="label-text">Postal Code</span>
            </label>
            <input
              id="postalCode"
              name="postalCode"
              type="text"
              autoComplete="off"
              placeholder="Enter postal code"
              maxLength={5}
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.postalCode}
              className={clsx(
                'input input-bordered w-full',
                formik.touched.postalCode &&
                  formik.errors.postalCode &&
                  'input-error'
              )}
            />
            {formik.touched.postalCode && formik.errors.postalCode && (
              <p className="mt-1 text-sm text-red-600">
                {formik.errors.postalCode}
              </p>
            )}
          </div>
          <div className="form-control">
            <label htmlFor="city" className="label">
              <span className="label-text">City</span>
            </label>
            <input
              id="city"
              name="city"
              type="text"
              placeholder="Enter city"
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.city}
              autoComplete="off"
              className={clsx(
                'input input-bordered w-full',
                formik.touched.city && formik.errors.city && 'input-error'
              )}
            />
            {formik.touched.city && formik.errors.city && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.city}</p>
            )}
          </div>
          <div className="form-control">
            <label htmlFor="state" className="label">
              <span className="label-text">State/Province</span>
            </label>
            <input
              id="state"
              name="state"
              type="text"
              placeholder="Enter state/province (optional)"
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.state}
              autoComplete="off"
              className={clsx(
                'input input-bordered w-full',
                formik.touched.state && formik.errors.state && 'input-error'
              )}
            />
            {formik.touched.state && formik.errors.state && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.state}</p>
            )}
          </div>
        </div>

        {/* Country */}
        <div className="form-control">
          <label htmlFor="country" className="label">
            <span className="label-text">Country</span>
          </label>
          <div ref={countryDropdownRef} className="relative">
            <div
              id="country"
              role="combobox"
              aria-expanded={isCountryDropdownOpen}
              aria-haspopup="listbox"
              aria-controls="country-listbox"
              aria-label="Country"
              aria-invalid={
                formik.touched.country && formik.errors.country
                  ? 'true'
                  : 'false'
              }
              aria-describedby={
                formik.touched.country && formik.errors.country
                  ? 'country-error'
                  : undefined
              }
              className={clsx(
                'input input-bordered w-full h-11 flex items-center justify-between',
                formik.touched.country &&
                  formik.errors.country &&
                  'input-error',
                isViewMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              )}
              onClick={() =>
                !isSubmitting &&
                !isViewMode &&
                setIsCountryDropdownOpen(!isCountryDropdownOpen)
              }
            >
              <span className="truncate">
                {sortedCountries.find((c) => c.cca2 === formik.values.country)
                  ?.name.common || 'Select country'}
              </span>
              {!isSubmitting && !isViewMode && (
                <KeenIcon
                  icon={isCountryDropdownOpen ? 'up' : 'down'}
                  className="text-gray-400"
                />
              )}
            </div>

            {isCountryDropdownOpen && !isSubmitting && !isViewMode && (
              <div
                id="country-listbox"
                role="listbox"
                className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden"
              >
                <div className="p-2 border-b border-gray-200 bg-gray-200">
                  <div className="relative">
                    <KeenIcon
                      icon="magnifier"
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      aria-hidden="true"
                    />
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearchTerm}
                      onChange={(e) => setCountrySearchTerm(e.target.value)}
                      className="input input-sm w-full pl-10 border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Search countries"
                    />
                  </div>
                </div>

                <div className="overflow-y-auto max-h-48" tabIndex={0}>
                  {sortedCountries
                    .filter((country) =>
                      country.name.common
                        .toLowerCase()
                        .includes(countrySearchTerm.toLowerCase())
                    )
                    .map((country) => (
                      <div
                        key={country.cca2}
                        role="option"
                        aria-selected={formik.values.country === country.cca2}
                        className="px-4 py-2.5 cursor-pointer hover:bg-gray-200 flex items-center gap-2"
                        onClick={() => {
                          formik.setFieldValue('country', country.cca2);
                          setIsCountryDropdownOpen(false);
                          setCountrySearchTerm('');
                        }}
                      >
                        <span className="text-sm">{country.name.common}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          {formik.touched.country && formik.errors.country && (
            <p
              id="country-error"
              role="alert"
              className="mt-1 text-sm text-red-600"
            >
              {formik.errors.country}
            </p>
          )}
        </div>

        {/* Bank Account Details */}
        <div className="border-t border-gray-200 dark:border-white/20 pt-6 mt-6">
          <h2 className="text-xl font-semibold text-primary dark:text-white contrast:text-white mb-4">
            Bank Account Details
          </h2>

          {/* Account Holder Name */}
          <div className="form-control mb-6">
            <label htmlFor="accountHolderName" className="label">
              <span className="label-text">Account Holder Name</span>
            </label>
            <input
              id="accountHolderName"
              name="accountHolderName"
              type="text"
              autoComplete="off"
              placeholder="Enter account holder name"
              disabled={isSubmitting || isViewMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accountHolderName}
              className={clsx(
                'input input-bordered w-full',
                formik.touched.accountHolderName &&
                  formik.errors.accountHolderName &&
                  'input-error'
              )}
            />
            {formik.touched.accountHolderName &&
              formik.errors.accountHolderName && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.accountHolderName}
                </p>
              )}
          </div>

          {/* Account Number and IBAN */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-6">
            <div className="form-control">
              <label htmlFor="accountNumber" className="label">
                <span className="label-text">Account Number</span>
              </label>
              <input
                id="accountNumber"
                name="accountNumber"
                type="text"
                autoComplete="off"
                placeholder="Enter account number"
                disabled={isSubmitting || isViewMode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.accountNumber}
                className={clsx(
                  'input input-bordered w-full',
                  formik.touched.accountNumber &&
                    formik.errors.accountNumber &&
                    'input-error'
                )}
              />
              {formik.touched.accountNumber && formik.errors.accountNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.accountNumber}
                </p>
              )}
            </div>
            <div className="form-control">
              <label htmlFor="iban" className="label">
                <span className="label-text">IBAN</span>
              </label>
              <input
                id="iban"
                name="iban"
                type="text"
                autoComplete="off"
                placeholder="Enter IBAN"
                disabled={isSubmitting || isViewMode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.toUpperCase().replace(/\s/g, '');
                  formik.setFieldValue('iban', value);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.iban}
                className={clsx(
                  'input input-bordered w-full uppercase',
                  formik.touched.iban && formik.errors.iban && 'input-error'
                )}
              />
              {formik.touched.iban && formik.errors.iban && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.iban}
                </p>
              )}
            </div>
          </div>

          {/* BIC and Bank Name */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="form-control">
              <label htmlFor="bic" className="label">
                <span className="label-text">BIC</span>
              </label>
              <input
                id="bic"
                name="bic"
                type="text"
                autoComplete="off"
                placeholder="Enter BIC"
                disabled={isSubmitting || isViewMode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.toUpperCase().replace(/\s/g, '');
                  formik.setFieldValue('bic', value);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.bic}
                className={clsx(
                  'input input-bordered w-full uppercase',
                  formik.touched.bic && formik.errors.bic && 'input-error'
                )}
              />
              {formik.touched.bic && formik.errors.bic && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.bic}</p>
              )}
            </div>
            <div className="form-control">
              <label htmlFor="bankName" className="label">
                <span className="label-text">Bank Name</span>
              </label>
              <input
                id="bankName"
                name="bankName"
                type="text"
                autoComplete="off"
                placeholder="Enter bank name"
                disabled={isSubmitting || isViewMode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.bankName}
                className={clsx(
                  'input input-bordered w-full',
                  formik.touched.bankName &&
                    formik.errors.bankName &&
                    'input-error'
                )}
              />
              {formik.touched.bankName && formik.errors.bankName && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.bankName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Admin Notes - Display in view mode */}
        {isViewMode && kycData && (
          <div className="border-t border-gray-200 dark:border-white/20 pt-6 mt-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Admin Notes</span>
              </label>
              <div className="mt-1 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 min-h-[100px] contrast:bg-black">
                {kycData.admin_notes ? (
                  <p className="text-sm text-[#C4CADA] dark:text-white whitespace-pre-line">
                    {kycData.admin_notes}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-600 italic">
                    No admin notes available
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {!isViewMode && (
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label="Cancel"
              disabled={isSubmitting || isViewMode}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={clsx(
                'btn btn-sm btn-primary rounded-lg px-4 py-2',
                'focus:outline-none focus:ring-2 focus:ring-primary-500',
                {
                  'opacity-50 cursor-not-allowed': isSubmitting,
                }
              )}
              aria-label={mode === 'edit' ? 'Update KYC' : 'Submit KYC'}
            >
              {isSubmitting
                ? mode === 'edit'
                  ? 'Updating...'
                  : 'Submitting...'
                : mode === 'edit'
                  ? 'Update'
                  : 'Submit'}
            </button>
          </div>
        )}
        {isViewMode && (
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-primary rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Back"
            >
              Back
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AddKYCForm;
